import { useInfiniteQuery, useQuery } from '@tanstack/react-query'

import { rqKeys } from '@/react-query-keys'
import type { T_UniRes } from '@/types'
import type { T_Transaction, T_TransactionsListRes } from '@/types/responses'
import { apiRequest } from '../request'


export const useTransactions = () => (
  useQuery<T_UniRes<T_TransactionsListRes>>({
    queryKey: [ rqKeys.transactionsKey ],
    queryFn: () => (
      apiRequest({
        method: 'money/account/transmit/list',
      })
    ),
  })
)

const TRANSACTIONS_PAGE_SIZE = 30
const MAX_OFFLINE_TRANSACTIONS_PAGES = 10
const MAX_CONSECUTIVE_DUPLICATE_PAGES = 2
const TRANSACTIONS_DEBUG =
  import.meta.env.DEV
  && typeof window !== 'undefined'
  && window.location.search.includes('debugTransactions=1')

const logTransactionsPaging = (phase: string, payload: Record<string, unknown> = {}) => {
  if (!TRANSACTIONS_DEBUG) return
  // eslint-disable-next-line no-console
  console.debug(`[transactions][paging] ${phase}`, payload)
}

const fetchTransactionsPage = async (page: number) => {
  const res = await apiRequest<T_UniRes<T_TransactionsListRes>>({
    method: 'money/account/transmit/list',
    param: {
      page,
      limit: TRANSACTIONS_PAGE_SIZE,
    },
  })
  logTransactionsPaging('page loaded', {
    page,
    limit: TRANSACTIONS_PAGE_SIZE,
    received: res?.data?.list?.length ?? 0,
    ids: (res?.data?.list ?? []).map((t) => t.id),
  })
  return res
}

const hasAnyNewAgainstSeenIds = (
  chunk: T_Transaction[],
  seenIds: Set<T_Transaction['id']>,
): boolean => {
  for (const transaction of chunk) {
    if (!seenIds.has(transaction.id)) return true
  }
  return false
}

export const useTransactionsInfinite = () => {
  const query = useInfiniteQuery<T_UniRes<T_TransactionsListRes>>({
    queryKey: [ rqKeys.transactionsKey, 'all' ],
    initialPageParam: 1,
    queryFn: ({ pageParam }) => fetchTransactionsPage(Number(pageParam)),
    getNextPageParam: (lastPage, allPages) => {
      const lastChunk = lastPage?.data?.list ?? []
      if (!lastChunk.length) {
        logTransactionsPaging('stop: empty page')
        return undefined
      }

      // O(N) incremental scan of pages with no slice/flatMap allocations.
      const seenIds = new Set<T_Transaction['id']>()
      let previousPageHadAnyNew = false
      let currentPageHadAnyNew = false
      for (let pageIndex = 0; pageIndex < allPages.length; pageIndex += 1) {
        const chunk = allPages[pageIndex]?.data?.list ?? []
        const pageHasAnyNew = hasAnyNewAgainstSeenIds(chunk, seenIds)
        if (pageIndex === allPages.length - 2) previousPageHadAnyNew = pageHasAnyNew
        if (pageIndex === allPages.length - 1) currentPageHadAnyNew = pageHasAnyNew
        for (const transaction of chunk) {
          seenIds.add(transaction.id)
        }
      }

      if (!currentPageHadAnyNew) {
        let duplicateTailCount = 1
        if (allPages.length >= 2 && !previousPageHadAnyNew) {
          duplicateTailCount = 2
        }

        logTransactionsPaging('duplicate page', {
          page: allPages.length,
          ids: lastChunk.map((t) => t.id),
          duplicateTailCount,
        })

        if (duplicateTailCount >= MAX_CONSECUTIVE_DUPLICATE_PAGES) {
          logTransactionsPaging('stop: duplicate pages limit reached', {
            duplicateTailCount,
            limit: MAX_CONSECUTIVE_DUPLICATE_PAGES,
          })
          return undefined
        }
      }
      const nextPage = allPages.length + 1
      logTransactionsPaging('next page', { nextPage })
      return nextPage
    },
  })

  const merged = (query.data?.pages ?? []).flatMap((page) => page?.data?.list ?? [])
  const transactionsList: T_Transaction[] = Array.from(
    new Map(merged.map((transaction) => [ transaction.id, transaction ])).values()
  )
  const offlineTransactionsList = transactionsList.slice(0, MAX_OFFLINE_TRANSACTIONS_PAGES * TRANSACTIONS_PAGE_SIZE)

  return {
    ...query,
    transactionsList,
    offlineTransactionsList,
  }
}
